import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";
import ConfirmAlert from "../../common/ConfirmAlert";
import { GlobalContext } from "../../context/GlobalContext";
import { VIDEO_STUDIO_CATEGORIES } from "./categories";
import { listVideoJobs } from "./api/listVideoJobs";
import { deleteVideoJob } from "./api/deleteVideoJob";
import { pollVideoJob } from "./api/generateVideo";
import {
  downloadVideoStudioJob,
  videoJobDownloadFilename,
} from "./api/downloadVideoStudioJob";
import VideoLibraryCard, { VideoLibraryCardSkeleton } from "./components/VideoLibraryCard";
import VideoLibraryUploadDialog from "./components/VideoLibraryUploadDialog";
import {
  filterJobsByStatus,
  formatJobDate,
  jobCategoryLabel,
  jobModelLabel,
  jobStatusMeta,
  truncatePrompt,
} from "./utils/jobLabels";

const PAGE_SIZE = 12;

const FILTER_TABS = [
  { id: "all", status: "", key: "videoLibraryFilterAll" },
  { id: "ready", status: "completed", key: "videoLibraryFilterReady" },
  { id: "progress", status: "processing", key: "videoLibraryFilterProgress" },
  { id: "failed", status: "failed", key: "videoLibraryFilterFailed" },
];

function navigateToUserPage(page, extra = {}) {
  const params = new URLSearchParams();
  params.set("page", page);
  Object.entries(extra).forEach(([k, v]) => {
    if (v != null && v !== "") params.set(k, String(v));
  });
  window.location.assign(`/user?${params.toString()}`);
}

export default function VideoLibrary({ lang }) {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [filterTab, setFilterTab] = React.useState(0);
  const [categoryFilter, setCategoryFilter] = React.useState("");
  const [listPage, setListPage] = React.useState(1);
  const [jobs, setJobs] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [polling, setPolling] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadProgress, setDownloadProgress] = React.useState(null);
  const fetchIdRef = React.useRef(0);

  const statusFilter = FILTER_TABS[filterTab]?.status || "";

  const notify = React.useCallback(
    (message, success = false) => {
      if (message) showSnack?.(message, success);
    },
    [showSnack],
  );

  const loadJobs = React.useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const { jobs: rows, pagination: pag } = await listVideoJobs(hitAxios, {
        page: listPage,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
      });
      if (fetchId !== fetchIdRef.current) return;

      if (pag && listPage > pag.total_pages && pag.total_pages > 0) {
        setListPage(pag.total_pages);
        return;
      }

      const filtered = filterJobsByStatus(rows, statusFilter);
      setJobs(filtered);
      setPagination(pag);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      notify(err?.message || lang?.videoLibraryLoadFailed || "Could not load videos.", false);
      setJobs([]);
      setPagination(null);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [hitAxios, listPage, statusFilter, categoryFilter, lang, notify]);

  React.useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  React.useEffect(() => {
    const onJob = (event) => {
      const job = event?.detail?.job;
      if (job?.type === "video-studio" && job?.phase === "complete") loadJobs();
    };
    window.addEventListener("beyundai:background-job", onJob);
    return () => window.removeEventListener("beyundai:background-job", onJob);
  }, [loadJobs]);

  const handleTabChange = (_, next) => {
    setJobs([]);
    setFilterTab(next);
    setListPage(1);
  };

  const handleCategoryFilterChange = (event) => {
    setJobs([]);
    setCategoryFilter(event.target.value);
    setListPage(1);
  };

  const handleOpenDetail = (job) => {
    setSelected(job);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const handleRefreshJob = async () => {
    if (!selected?.id) return;
    setPolling(true);
    try {
      const job = await pollVideoJob(hitAxios, selected.id);
      setSelected(job);
      notify(lang?.videoLibraryRefreshed || "Status updated.", true);
      loadJobs();
    } catch (err) {
      notify(err?.message || "Could not refresh job.", false);
    } finally {
      setPolling(false);
    }
  };

  const requestDelete = (job) => {
    if (!job?.id) return;
    setDeleteTarget(job);
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id || deleting) return;
    setDeleting(true);
    try {
      await deleteVideoJob(hitAxios, deleteTarget.id);
      notify(lang?.videoLibraryDeleted || "Video removed from your library.", true);
      if (selected?.id === deleteTarget.id) {
        handleCloseDetail();
      }
      setDeleteTarget(null);
      loadJobs();
    } catch (err) {
      notify(err?.message || lang?.videoLibraryDeleteFailed || "Could not delete video.", false);
    } finally {
      setDeleting(false);
    }
  };

  const copyJobLink = () => {
    if (!selected?.id) return;
    const url = `${window.location.origin}/user?page=video-studio&job=${selected.id}`;
    navigator.clipboard?.writeText(url).then(
      () => notify(lang?.videoStudioShareCopied || "Link copied to clipboard.", true),
      () => notify("Copy failed", false),
    );
  };

  const handleDownload = async () => {
    if (!selected?.id || downloading) return;
    setDownloading(true);
    setDownloadProgress({ phase: "preparing", percent: 0 });
    try {
      await downloadVideoStudioJob(selected.id, videoJobDownloadFilename(selected), {
        onProgress: (info) => setDownloadProgress(info),
      });
      notify(lang?.videoStudioDownloadStarted || "Download started.", true);
    } catch (err) {
      notify(
        err?.message || lang?.videoStudioDownloadFailed || "Download failed. Try again.",
        false,
      );
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  };

  const downloadLabel = (() => {
    if (!downloading) return lang?.videoStudioDownload || "Download";
    const phase = downloadProgress?.phase;
    const pct = downloadProgress?.percent;
    if (phase === "preparing") return lang?.videoStudioDownloadPreparing || "Preparing…";
    if (phase === "saving") return lang?.videoStudioDownloadSaving || "Saving…";
    if (typeof pct === "number") {
      return `${lang?.videoStudioDownloading || "Downloading"} ${pct}%`;
    }
    return lang?.videoStudioDownloading || "Downloading…";
  })();

  const selectedMeta = selected ? jobStatusMeta(selected.status, lang) : null;
  const showProgressTabNote = filterTab === 2 && statusFilter === "processing";

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={lang?.videoLibraryTitle || "Video Library"}
        subtitle={
          lang?.videoLibrarySubtitle ||
          "Browse, upload, and play videos from Video Studio and your own files."
        }
        icon={VideoLibraryOutlined}
        gradientIcon
        secondaryActions={
          <Button
            variant="outlined"
            startIcon={<CloudUploadOutlined />}
            onClick={() => setUploadOpen(true)}
            sx={{ fontWeight: 700 }}
          >
            {lang?.videoLibraryUpload || "Upload video"}
          </Button>
        }
        primaryAction={
          <Button
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={() => navigateToUserPage("video-studio")}
            sx={{
              background: "linear-gradient(135deg, #A78BFA 0%, #EC4899 50%, #F97316 100%)",
              fontWeight: 700,
            }}
          >
            {lang?.videoLibraryNewVideo || "New video"}
          </Button>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Tabs
          value={filterTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 700 },
          }}
        >
          {FILTER_TABS.map((t) => (
            <Tab key={t.id} label={lang?.[t.key] || t.id} />
          ))}
        </Tabs>

        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
          <InputLabel id="video-lib-category-filter">
            {lang?.videoLibraryCategoryFilter || "Use case"}
          </InputLabel>
          <Select
            labelId="video-lib-category-filter"
            label={lang?.videoLibraryCategoryFilter || "Use case"}
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
          >
            <MenuItem value="">{lang?.videoLibraryFilterAll || "All"}</MenuItem>
            {VIDEO_STUDIO_CATEGORIES.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {lang?.[cat.labelKey] || cat.fallback}
              </MenuItem>
            ))}
            <MenuItem value="stitch">
              {lang?.videoLibraryCatStitch || "Stitched video"}
            </MenuItem>
            <MenuItem value="upload">
              {lang?.videoLibraryCatUpload || "Uploaded video"}
            </MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {showProgressTabNote ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lang?.videoLibraryProgressHint ||
            "Shows jobs currently generating. Pending jobs appear under All."}
        </Typography>
      ) : null}

      {!loading && jobs.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 3,
            textAlign: "center",
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <VideoLibraryOutlined sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" fontWeight={800} gutterBottom>
            {lang?.videoLibraryEmptyTitle || "No videos yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: "auto" }}>
            {lang?.videoLibraryEmptyBody ||
              "Generate a video in Video Studio or upload your own MP4 — it will show up here."}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<CloudUploadOutlined />}
              onClick={() => setUploadOpen(true)}
            >
              {lang?.videoLibraryUpload || "Upload video"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => navigateToUserPage("video-studio")}
            >
              {lang?.videoLibraryNewVideo || "New video"}
            </Button>
          </Stack>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`sk-${i}`}>
                    <VideoLibraryCardSkeleton />
                  </Grid>
                ))
              : jobs.map((job) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={job.id}>
                    <VideoLibraryCard
                      job={job}
                      lang={lang}
                      onClick={handleOpenDetail}
                      onDelete={requestDelete}
                    />
                  </Grid>
                ))}
          </Grid>

          {pagination && pagination.total_pages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={pagination.total_pages}
                page={listPage}
                onChange={(_, p) => setListPage(p)}
                color="primary"
                shape="rounded"
              />
            </Box>
          ) : null}
        </>
      )}

      <CommonDialog
        open={detailOpen}
        onClose={handleCloseDetail}
        title={lang?.videoLibraryDetailTitle || "Video details"}
        icon={VideoLibraryOutlined}
        maxWidth="md"
      >
        {selected ? (
          <Stack spacing={2}>
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <Chip label={selectedMeta?.label} color={selectedMeta?.color} size="small" />
              <Chip
                label={jobCategoryLabel(selected.category_id, lang)}
                variant="outlined"
                size="small"
              />
              <Chip label={jobModelLabel(selected.ui_model, lang)} variant="outlined" size="small" />
              <Typography variant="caption" color="text.secondary">
                {formatJobDate(selected.created_at)}
              </Typography>
            </Stack>

            <Box
              sx={{
                position: "relative",
                pt: "56.25%",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: alpha(theme.palette.common.black, 0.9),
              }}
            >
              {selected.status === "completed" && selected.video_url ? (
                <Box
                  component="video"
                  src={selected.video_url}
                  controls
                  playsInline
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                  }}
                >
                  <Typography variant="body2" color="grey.400" textAlign="center">
                    {selected.status === "failed"
                      ? selected.error_message ||
                        lang?.videoLibraryNoPreviewFailed ||
                        "Generation failed — no video to preview."
                      : lang?.videoLibraryNoPreviewYet ||
                        "Video is still processing. Refresh status to check again."}
                  </Typography>
                </Box>
              )}
            </Box>

            {selected.prompt ? (
              <Typography variant="body2" color="text.secondary">
                {truncatePrompt(selected.prompt, 2000)}
              </Typography>
            ) : null}

            <Stack direction="row" flexWrap="wrap" gap={1}>
              {(selected.status === "pending" || selected.status === "generating") && (
                <Button
                  variant="outlined"
                  startIcon={<RefreshOutlined />}
                  onClick={handleRefreshJob}
                  disabled={polling}
                >
                  {polling
                    ? lang?.videoLibraryRefreshing || "Refreshing…"
                    : lang?.videoLibraryRefreshStatus || "Refresh status"}
                </Button>
              )}
              {selected.status === "completed" && selected.video_url ? (
                <Button
                  variant="contained"
                  onClick={handleDownload}
                  disabled={downloading}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadOutlined />
                    )
                  }
                >
                  {downloadLabel}
                </Button>
              ) : null}
              <Button variant="outlined" startIcon={<ContentCopyOutlined />} onClick={copyJobLink}>
                {lang?.videoStudioShare || "Share"}
              </Button>
              <Button
                variant="text"
                startIcon={<OpenInNewOutlined />}
                onClick={() => navigateToUserPage("video-studio", { job: selected.id })}
              >
                {lang?.videoLibraryOpenStudio || "Open in Video Studio"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutline />}
                onClick={() => requestDelete(selected)}
              >
                {lang?.videoLibraryDelete || "Delete"}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </CommonDialog>

      <VideoLibraryUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        lang={lang}
        hitAxios={hitAxios}
        notify={notify}
        onUploaded={() => {
          setFilterTab(1);
          setListPage(1);
        }}
      />

      <ConfirmAlert
        open={Boolean(deleteTarget)}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        title={lang?.videoLibraryDeleteTitle || "Delete from library?"}
        message={
          lang?.videoLibraryDeleteConfirm ||
          "This removes the job from your library. This action cannot be undone."
        }
        confirmLabel={lang?.videoLibraryDelete || "Delete"}
        cancelLabel={lang?.cancel || "Cancel"}
        confirmColor="error"
        icon={DeleteOutline}
      />
    </Box>
  );
}
